/**
 * Phase 13 / Task 13.3 (DEC-351) — pure helpers for the dispatcher Tanks tab.
 */

import { isHistoryTooShort } from '@lodex-pro/shared/tank-fill-rate';
import { formatTankReading } from '@lodex-pro/shared/tank-level-format';
import type { TankLevelRow } from './use-tank-levels';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** BBL number → "142" / "1,250.5" (at most one decimal: an estimate, not a ticket). */
export function formatEstimateBbl(bbl: number): string {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(bbl);
}

/** "~142 BBL (est.)" — the headline when an estimate exists. */
export function estimateHeadline(bbl: number): string {
  return `~${formatEstimateBbl(bbl)} BBL (est.)`;
}

/**
 * "full in about 3 days" from a predicted-full ISO time. Rounded to whole days past the
 * first; under a day says so; a time already passed means it may be full now.
 */
export function fullInLabel(predictedFullAt: string, now: Date = new Date()): string {
  const ms = Date.parse(predictedFullAt) - now.getTime();
  if (!Number.isFinite(ms)) return '';
  if (ms <= 0) return 'may be full now';
  const days = ms / MS_PER_DAY;
  if (days < 1) return 'full in under a day';
  const whole = Math.round(days);
  return `full in about ${whole} ${whole === 1 ? 'day' : 'days'}`;
}

/**
 * Phase 13.4a — when the tank reaches its haul threshold: "reaches it in about 2 days", "at or over
 * it now", or "no forecast yet" (no rate or level).
 */
export function thresholdInLabel(
  predictedThresholdAt: string | null,
  now: Date = new Date(),
): string {
  if (predictedThresholdAt === null) return 'no forecast yet';
  const ms = Date.parse(predictedThresholdAt) - now.getTime();
  if (!Number.isFinite(ms)) return '';
  if (ms <= 0) return 'at or over it now';
  const days = ms / MS_PER_DAY;
  if (days < 1) return 'reaches it in under a day';
  const whole = Math.round(days);
  return `reaches it in about ${whole} ${whole === 1 ? 'day' : 'days'}`;
}

/**
 * Phase 13.4b (0237) — why auto-haul did not create a job for a due tank, in a few words for the
 * Tanks tab (the sweeper's notification says more). Unknown codes read as a plain hold.
 */
export function autoHaulHoldLabel(reason: string): string {
  switch (reason) {
    case 'ESTIMATES_OFF':
      return 'auto-haul held: forecasts are off';
    case 'NO_RECENT_READING':
      return 'auto-haul held: no reading in 30 days';
    case 'STALE_PREDICTION':
      return 'auto-haul held: forecast is out of date';
    case 'OVER_AFTER_HAUL':
      return 'auto-haul held: still over after the last haul; enter a reading';
    case 'AUTO_JOB_CANCELLED':
      return 'auto-haul held: last auto job was cancelled; enter a reading';
    case 'JOB_TYPE_UNAVAILABLE':
      return 'auto-haul held: job type is no longer active';
    case 'JOB_TYPE_NOT_FOR_CUSTOMER':
      return 'auto-haul held: job type is not offered to this customer';
    case 'PICKUP_UNAVAILABLE':
      return 'auto-haul held: the site cannot take a job';
    case 'ERROR':
      return 'auto-haul held: it keeps failing on this tank; support has been alerted';
    default:
      return 'auto-haul held';
  }
}

/**
 * 13.8 (D-23c) — the producer's latest reading is a level with no barrels (their tank has no
 * size of its own yet): the headline is that level RAW, "8 ft 4 in, size unknown". Null
 * otherwise. Never a barrel estimate: nothing converts it but the producer's own size.
 */
export function producerRawHeadline(row: TankLevelRow): string | null {
  const r = row.latestReading ?? null;
  if (row.operatorNeedsSize !== true || r === null) return null;
  if (r.volumeBbl !== null || r.levelIn === null) return null;
  return formatTankReading(r);
}

/** "Fills about 12.5 BBL/day". */
export function fillRateLabel(bpd: number): string {
  return `Fills about ${formatEstimateBbl(bpd)} BBL/day`;
}

/** Why a row has no estimate, in words a dispatcher can act on; null when it has one. */
export function noEstimateReason(row: TankLevelRow, estimatesEnabled: boolean): string | null {
  if (!estimatesEnabled) return null;
  if (row.estimatedCurrentBbl !== null) return null;
  if (!row.hasStrapping) return 'No estimate: set BBL per inch on this tank';
  if (row.lastHaulAt === null) return 'No hauls yet';
  return 'Not enough history';
}

// ── The forecast as shown (2026-10-21) ─────────────────────────────────────────────────────

/** What the tab shows for a tank's rate and projections. */
export interface ShownForecast {
  readonly fillRateBpd: number | null;
  readonly predictedFullAt: string | null;
  readonly predictedThresholdAt: string | null;
  /** The readings cover less than MIN_HISTORY_SPAN_HOURS: "Not enough history", no projection. */
  readonly shortHistory: boolean;
}

/**
 * The shared display rule (#1013 on the phone, here on the web): readings that span less than
 * MIN_HISTORY_SPAN_HOURS show no rate, no full time and no threshold forecast. Display only; the
 * auto-haul sweeper reads the API's own values.
 */
export function shownForecast(row: TankLevelRow): ShownForecast {
  if (isHistoryTooShort(row.historySpanHours)) {
    return {
      fillRateBpd: null,
      predictedFullAt: null,
      predictedThresholdAt: null,
      shortHistory: true,
    };
  }
  return {
    fillRateBpd: row.fillRateBpd,
    predictedFullAt: row.predictedFullAt,
    predictedThresholdAt: row.predictedThresholdAt ?? null,
    shortHistory: false,
  };
}

// ── Sorting ──────────────────────────────────────────────────────────────────────────────────

export type TankSortKey = 'TANK' | 'ESTIMATE' | 'PERCENT' | 'FULL_BY' | 'LAST_HAUL' | 'THRESHOLD';
export type SortDir = 'asc' | 'desc';
export interface TankSortState {
  readonly key: TankSortKey;
  readonly dir: SortDir;
}

/** Soonest full first: the tab's default (DEC-351). */
export const DEFAULT_TANK_SORT: TankSortState = { key: 'FULL_BY', dir: 'asc' };

/** The first click on a column sorts it the useful way round; a second click reverses it. */
export const FIRST_DIR: Readonly<Record<TankSortKey, SortDir>> = {
  TANK: 'asc',
  ESTIMATE: 'desc',
  PERCENT: 'desc',
  FULL_BY: 'asc',
  LAST_HAUL: 'desc',
  THRESHOLD: 'asc',
};

export function nextSort(current: TankSortState, key: TankSortKey): TankSortState {
  if (current.key !== key) return { key, dir: FIRST_DIR[key] };
  return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
}

const time = (iso: string | null | undefined): number | null => {
  if (iso === null || iso === undefined) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
};

/** The value a column sorts on; null sorts last whichever way round. */
function sortValue(
  row: TankLevelRow,
  key: Exclude<TankSortKey, 'TANK'>,
  estimatesEnabled: boolean,
): number | null {
  const f = shownForecast(row);
  switch (key) {
    case 'ESTIMATE':
      return estimatesEnabled ? row.estimatedCurrentBbl : row.lastLevelBbl;
    case 'PERCENT':
      return row.estimatedPercentFull;
    case 'FULL_BY':
      return time(f.predictedFullAt);
    case 'LAST_HAUL':
      return time(row.lastHaulAt);
    case 'THRESHOLD':
      // When it reaches its threshold (soonest first); a tank with none, or no forecast, last.
      return row.thresholdPercent === null || row.thresholdPercent === undefined
        ? null
        : time(f.predictedThresholdAt);
  }
}

const byName = (a: TankLevelRow, b: TankLevelRow): number =>
  a.tankName.localeCompare(b.tankName, 'en', { numeric: true }) ||
  (a.siteName ?? '').localeCompare(b.siteName ?? '', 'en', { numeric: true });

/** Sorted by one column; ties and empty values by name, empty values always last. Never mutates. */
export function sortTanks(
  tanks: readonly TankLevelRow[],
  sort: TankSortState,
  estimatesEnabled = true,
): readonly TankLevelRow[] {
  const sign = sort.dir === 'asc' ? 1 : -1;
  const out = [...tanks];
  if (sort.key === 'TANK') return out.sort((a, b) => sign * byName(a, b));
  const key = sort.key;
  return out.sort((a, b) => {
    const va = sortValue(a, key, estimatesEnabled);
    const vb = sortValue(b, key, estimatesEnabled);
    if (va === null && vb === null) return byName(a, b);
    if (va === null) return 1;
    if (vb === null) return -1;
    return sign * (va - vb) || byName(a, b);
  });
}

// ── Filters ──────────────────────────────────────────────────────────────────────────────────

export type TankStatus =
  | 'MAY_BE_FULL'
  | 'OVER_THRESHOLD'
  | 'HAS_ESTIMATE'
  | 'NO_ESTIMATE'
  | 'NO_HAULS';

export const TANK_STATUSES: readonly { id: TankStatus; label: string }[] = [
  { id: 'MAY_BE_FULL', label: 'May be full now' },
  { id: 'OVER_THRESHOLD', label: 'Over threshold' },
  { id: 'HAS_ESTIMATE', label: 'Has estimate' },
  { id: 'NO_ESTIMATE', label: 'No estimate' },
  { id: 'NO_HAULS', label: 'No hauls yet' },
];

export interface TankFilters {
  /** Matches the tank, its site/well and its customer, case-insensitive. */
  readonly q: string;
  /** A customer name; '' = all. */
  readonly customer: string;
  /** A site/well name; '' = all. */
  readonly site: string;
  /** Any of these (OR); empty = all. */
  readonly statuses: readonly TankStatus[];
}

export const NO_TANK_FILTERS: TankFilters = { q: '', customer: '', site: '', statuses: [] };

export function hasTankFilters(f: TankFilters): boolean {
  return f.q.trim() !== '' || f.customer !== '' || f.site !== '' || f.statuses.length > 0;
}

/** Which status chips a tank matches now. */
export function tankStatuses(row: TankLevelRow, now: Date = new Date()): ReadonlySet<TankStatus> {
  const f = shownForecast(row);
  const out = new Set<TankStatus>();
  const full = time(f.predictedFullAt);
  if (full !== null && full <= now.getTime()) out.add('MAY_BE_FULL');
  const crossing = time(f.predictedThresholdAt);
  if (row.thresholdPercent != null && crossing !== null && crossing <= now.getTime()) {
    out.add('OVER_THRESHOLD');
  }
  out.add(row.estimatedCurrentBbl !== null ? 'HAS_ESTIMATE' : 'NO_ESTIMATE');
  if (row.lastHaulAt === null) out.add('NO_HAULS');
  return out;
}

export function filterTanks(
  tanks: readonly TankLevelRow[],
  f: TankFilters,
  now: Date = new Date(),
): readonly TankLevelRow[] {
  const q = f.q.trim().toLowerCase();
  return tanks.filter((t) => {
    if (q !== '') {
      const hay = [t.tankName, t.siteName ?? '', t.customerName ?? ''].join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (f.customer !== '' && (t.customerName ?? '') !== f.customer) return false;
    if (f.site !== '' && (t.siteName ?? '') !== f.site) return false;
    if (f.statuses.length > 0) {
      const has = tankStatuses(t, now);
      if (!f.statuses.some((s) => has.has(s))) return false;
    }
    return true;
  });
}

/** The distinct non-empty values for a filter's dropdown, A to Z. */
export function filterOptions(values: Iterable<string | null | undefined>): string[] {
  const set = new Set<string>();
  for (const v of values) if (v !== null && v !== undefined && v.trim() !== '') set.add(v);
  return [...set].sort((a, b) => a.localeCompare(b, 'en', { numeric: true }));
}

// ── Remembered per user and company in this browser (as the map layers, map-layer-prefs.ts) ──

export interface TankView {
  readonly sort: TankSortState;
  readonly filters: TankFilters;
}

export const DEFAULT_TANK_VIEW: TankView = { sort: DEFAULT_TANK_SORT, filters: NO_TANK_FILTERS };

/** Per user AND company (review L1): one person working for two haulers keeps two views. */
export const tankViewKey = (userId: string, tenantId: string): string =>
  `lodex.tanks.view.${userId}.${tenantId}`;

const known = (id: string | null | undefined): id is string =>
  id !== null && id !== undefined && id !== '';

const SORT_KEYS = new Set<string>(Object.keys(FIRST_DIR));
const STATUS_IDS = new Set<string>(TANK_STATUSES.map((s) => s.id));

/**
 * The stored view for this user at this company, or the default when either is unknown, nothing
 * is stored, it is unreadable or storage is blocked.
 */
export function readTankView(
  userId: string | null | undefined,
  tenantId: string | null | undefined,
  storage?: Storage,
): TankView {
  if (!known(userId) || !known(tenantId)) return DEFAULT_TANK_VIEW;
  try {
    const raw = (storage ?? window.localStorage).getItem(tankViewKey(userId, tenantId));
    if (raw === null) return DEFAULT_TANK_VIEW;
    const o = JSON.parse(raw) as Record<string, unknown>;
    const sort = (o['sort'] ?? {}) as Record<string, unknown>;
    const filters = (o['filters'] ?? {}) as Record<string, unknown>;
    const str = (v: unknown): string => (typeof v === 'string' ? v : '');
    const key = sort['key'];
    const dir = sort['dir'];
    return {
      sort:
        typeof key === 'string' && SORT_KEYS.has(key) && (dir === 'asc' || dir === 'desc')
          ? { key: key as TankSortKey, dir }
          : DEFAULT_TANK_SORT,
      filters: {
        q: str(filters['q']),
        customer: str(filters['customer']),
        site: str(filters['site']),
        statuses: Array.isArray(filters['statuses'])
          ? (filters['statuses'] as unknown[]).filter(
              (s): s is TankStatus => typeof s === 'string' && STATUS_IDS.has(s),
            )
          : [],
      },
    };
  } catch {
    return DEFAULT_TANK_VIEW;
  }
}

/** Store the view for this user at this company. Failure is silent: it lasts for this page only. */
export function writeTankView(
  userId: string | null | undefined,
  tenantId: string | null | undefined,
  view: TankView,
  storage?: Storage,
): void {
  if (!known(userId) || !known(tenantId)) return;
  try {
    (storage ?? window.localStorage).setItem(tankViewKey(userId, tenantId), JSON.stringify(view));
  } catch {
    // Storage blocked or full: keep working without remembering.
  }
}

/**
 * A remembered Customer or Site/well that no tank has any more is dropped (review L2): the
 * dropdown could not show it, so it would hide every tank with no visible reason.
 */
export function pruneTankFilters(
  f: TankFilters,
  customers: readonly string[],
  sites: readonly string[],
): TankFilters {
  const customer = f.customer !== '' && !customers.includes(f.customer) ? '' : f.customer;
  const site = f.site !== '' && !sites.includes(f.site) ? '' : f.site;
  return customer === f.customer && site === f.site ? f : { ...f, customer, site };
}
